import React, { useEffect, useState } from "react";
import { api, formatErr } from "./lib/api";
import { formatKSADate } from "./lib/ksaTime";

const SEG_META = {
  champions:  { ic: "🏆", lbl: "أبطال",       cls: "seg-champions", desc: "زاروا مؤخراً، يدفعون كثيراً، ويأتون باستمرار. الأفضل في القاعدة كلها.", action: "كافئهم بمنتج VIP أو خصم حصري لإبقائهم شركاء لمقهاك." },
  loyal:      { ic: "💙", lbl: "أوفياء",       cls: "seg-loyal", desc: "يأتون بانتظام وينفقون بمعدّل ثابت — لكن ليسوا أعلى المنفقين.", action: "ادفعهم لشرائح أعلى ببرنامج نقاط أو عرض اشتراك شهري." },
  new:        { ic: "🌱", lbl: "عملاء جدد",    cls: "seg-new", desc: "زاروا للمرة الأولى مؤخراً (آخر 30 يوم). فرصة ذهبية لجذبهم.", action: "أرسل رسالة ترحيب + خصم 20٪ على زيارة ثانية خلال أسبوع." },
  potential:  { ic: "✨", lbl: "محتملون",      cls: "seg-potential", desc: "زاروا 2-3 مرات بأرقام جيدة لكن لم يصبحوا أوفياء بعد.", action: "ذكّرهم بإشعار/SMS بعد 10-14 يوم من آخر زيارة لتسريع تحوّلهم." },
  at_risk:    { ic: "⚠",  lbl: "مهددون",       cls: "seg-at_risk", desc: "كانوا منفقين جيدين، لكن لم يزوروك منذ 30-60 يوم. على وشك الضياع.", action: "حملة استرجاع: خصم 30٪ أو دعوة مجانية لاسترداد علاقتهم." },
  lost:       { ic: "💔", lbl: "مفقودون",       cls: "seg-lost", desc: "لم يزوروك منذ أكثر من 60 يوم. غالباً انتقلوا لمقهى آخر.", action: "آخر محاولة: عرض مغرٍ جداً (كوب مجاني). لو ما رجعوا — اشطبهم." },
};

const fm = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");

function HeatCell({ value, max }) {
  const ratio = max ? value / max : 0;
  let bg = "rgba(243, 235, 221, .6)";
  if (ratio > 0) {
    const alpha = 0.15 + ratio * 0.85;
    bg = `rgba(194, 169, 127, ${alpha})`;
  }
  return (
    <div className="heatmap-cell" style={{ background: bg }} title={`${fm(value)} ر`}>
    </div>
  );
}

export default function Advanced() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [hourAi, setHourAi] = useState(null);
  const [hourLoading, setHourLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/analytics/all");
        setData(d);
      } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  const fetchHourAi = async () => {
    setHourLoading(true);
    try {
      const { data: r } = await api.post("/ai/hour-insights");
      setHourAi(r.text);
    } catch (e) {
      setHourAi(`⚠ ${formatErr(e.response?.data?.detail) || e.message}`);
    } finally { setHourLoading(false); }
  };

  if (loading) return <div className="card" style={{ padding: 30, textAlign: "center" }}><span className="spinner" /> جارٍ حساب التحليلات...</div>;
  if (err) return <div className="al alr">⚠ {err}</div>;
  if (!data) return null;

  const { heatmap, rfm, cohort, anomalies, forecast, forecast_shift, clv, best_hours, churn, registration, phone_coverage } = data;

  // Heatmap max
  const maxVal = Math.max(0, ...(heatmap.rev || []).flat());

  const phoneCoveragePct = phone_coverage?.pct || 0;
  const showPhoneWarning = phoneCoveragePct < 50;

  return (
    <div data-testid="advanced-tab">
      {showPhoneWarning && (
        <div className="al" style={{ background: "var(--warn-soft, #FEF3C7)", border: "1px solid #F59E0B", color: "#92400E", padding: 12, borderRadius: 10, marginBottom: 14, fontSize: 13.5, lineHeight: 1.7 }} data-testid="phone-coverage-warning">
          <b>⚠ تحذير: تحليل العملاء قد لا يكون دقيقاً</b>
          <br />
          {phone_coverage.total_invoices.toLocaleString("en-US")} فاتورة، منها <b>{phone_coverage.without_phone.toLocaleString("en-US")}</b> بدون رقم جوال
          ({phoneCoveragePct}% فقط معها جوال). هؤلاء عملاء غير مسجلين في رتم.
          <br />
          <b>الحل:</b> ارفع تقرير الفواتير من رتم بعد التأكد من تضمين عمود «رقم الجوال» — وإلا فإن الأسماء المتكررة (مثل «محمد») تُحسب كعميل واحد بـ {clv?.top?.[0]?.visits || 0} زيارة وهذا غير صحيح.
        </div>
      )}
      {/* HEATMAP */}
      <div className="st">🔥 الخريطة الحرارية — أيام × ساعات</div>
      <div className="card">
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
          الذروة: <b style={{ color: "var(--gold-deep)" }}>{heatmap.peak?.day} الساعة {heatmap.peak?.hour}:00</b>
          {heatmap.peak && <> · إيراد {fm(heatmap.peak.rev)} ر · {fm(heatmap.peak.count)} فاتورة</>}
        </div>
        <div className="heatmap-wrap">
          <div></div>
          <div className="heatmap-hours">
            {Array.from({ length: 24 }).map((_, h) => (
              <div key={`hr-${h}`}>{h % 3 === 0 ? `${h}` : ""}</div>
            ))}
          </div>
          {heatmap.days.map((dayName, di) => (
            <React.Fragment key={`day-${dayName}`}>
              <div className="heatmap-day">{dayName}</div>
              <div className="heatmap-grid">
                {(heatmap.rev[di] || []).map((v, h) => (
                  <HeatCell key={`${dayName}-h${h}`} value={v} max={maxVal} />
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* RFM */}
      <div className="st" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <span>🎯 شرائح العملاء (RFM)</span>
        <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 400 }}>
          تصنيف تلقائي حسب: آخر زيارة (R) · تكرار الزيارات (F) · إجمالي الإنفاق (M)
        </span>
      </div>

      {/* Methodology explainer — collapsible */}
      <details className="card" data-testid="rfm-explainer" style={{ marginBottom: 12, padding: 12, background: "var(--beige)" }}>
        <summary style={{ cursor: "pointer", fontWeight: 700, color: "var(--brown-deep)", fontSize: 13 }}>
          ❓ ما معنى هذه الشرائح؟ (اضغط للتفصيل)
        </summary>
        <div style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.9, color: "var(--brown)" }}>
          <p style={{ margin: "0 0 8px" }}>
            <b>RFM</b> هو تصنيف عالمي يُستخدم في التجزئة وقطاع F&amp;B، يُقيّم كل عميل على 3 محاور:
          </p>
          <ul style={{ margin: "0 0 10px 16px", padding: 0 }}>
            <li><b>R — Recency (الحداثة):</b> كم يوم مضى منذ آخر زيارة؟ (أقل = أفضل)</li>
            <li><b>F — Frequency (التكرار):</b> كم مرة زارك خلال الفترة؟ (أكثر = أفضل)</li>
            <li><b>M — Monetary (الإنفاق):</b> كم دفع إجمالاً؟ (أكثر = أفضل)</li>
          </ul>
          <p style={{ margin: "0 0 8px" }}>
            كل عميل يأخذ درجة من 1 (الأسوأ) إلى 5 (الأفضل) في كل محور، ثم يُصنّف تلقائياً في إحدى 6 شرائح:
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 8, marginTop: 8 }}>
            {Object.entries(SEG_META).map(([k, m]) => (
              <div key={k} style={{ padding: 8, background: "var(--card)", borderRadius: 6, border: "1px solid var(--border)", fontSize: 11.5 }}>
                <div style={{ fontWeight: 700, color: "var(--brown-deep)", marginBottom: 3 }}>
                  {m.ic} {m.lbl} <span style={{ color: "var(--muted)", fontWeight: 400, fontSize: 10.5 }}>({fm(rfm.segments?.[k] || 0)} عميل)</span>
                </div>
                <div style={{ color: "var(--muted)", marginBottom: 4 }}>{m.desc}</div>
                <div style={{ color: "var(--success)", fontSize: 11, fontWeight: 600 }}>💡 {m.action}</div>
              </div>
            ))}
          </div>
        </div>
      </details>

      <div className="seg-grid" data-testid="rfm-segments">
        {Object.entries(SEG_META).map(([k, m]) => (
          <div
            key={k}
            className={`seg-card ${m.cls}`}
            data-testid={`seg-${k}`}
            title={`${m.desc}\n\n💡 ${m.action}`}
            style={{ cursor: "help" }}
          >
            <div className="seg-icn">{m.ic}</div>
            <div className="seg-name">{m.lbl}</div>
            <div className="seg-cnt">{fm(rfm.segments?.[k] || 0)}</div>
          </div>
        ))}
      </div>

      {rfm.top?.length > 0 && (
        <>
          <div className="st">💎 أكثر العملاء قيمةً (Top 20)</div>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>العميل</th>
                  <th>الإنفاق الكلي</th>
                  <th>الزيارات</th>
                  <th>آخر زيارة (أيام)</th>
                  <th>الشريحة</th>
                </tr>
              </thead>
              <tbody>
                {rfm.top.map((r, i) => (
                  <tr key={`${r.name}-${r.monetary}-${i}`}>
                    <td>{i + 1}</td>
                    <td style={{ fontWeight: 600, textAlign: "right" }}>{r.name}</td>
                    <td className="num">{fm(r.monetary)} ر</td>
                    <td>{fm(r.frequency)}</td>
                    <td>{fm(r.recency)}</td>
                    <td><span className="b bg" style={{ background: SEG_META[r.segment]?.cls.includes("at_risk") ? "var(--warn-soft)" : "var(--success-soft)", color: "var(--brown)" }}>{SEG_META[r.segment]?.ic} {SEG_META[r.segment]?.lbl}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* COHORT */}
      {cohort.cohorts?.length > 0 && (
        <>
          <div className="st">📈 تتبع رجوع العملاء (Retention Cohort)</div>
          <div className="card">
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
              نسبة العملاء اللي رجعوا في كل أسبوع تابع لأسبوع زيارتهم الأولى.
            </div>
            <div className="cohort-table">
              <div className="cohort-row" style={{ fontWeight: 700, color: "var(--muted)" }}>
                <div>الأسبوع</div>
                {Array.from({ length: 8 }).map((_, i) => <div key={`hdr-w${i}`} style={{ textAlign: "center", fontSize: 10 }}>أ{i}</div>)}
              </div>
              {cohort.cohorts.map((c) => (
                <div key={c.cohort} className="cohort-row">
                  <div className="cohort-label">{c.cohort.slice(5)} ({c.size})</div>
                  {c.retention.map((r, w) => (
                    <div key={`${c.cohort}-w${w}`} className={`cohort-cell ${r === 0 ? "empty" : ""}`} style={{
                      background: r > 0 ? `rgba(16,185,129,${Math.max(0.08, r / 100 * 0.9)})` : "transparent",
                      color: r > 50 ? "#fff" : "var(--brown-deep)",
                    }}>
                      {r > 0 ? `${r}%` : "—"}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ANOMALIES */}
      <div className="st">⚡ كشف الشذوذ (Anomalies)</div>
      <div className="card">
        {anomalies.length === 0 ? (
          <div style={{ padding: 14, color: "var(--success)", textAlign: "center", fontSize: 13 }}>✅ لا توجد أيام شاذة في الفترة الأخيرة.</div>
        ) : (
          <div className="anom-list">
            {anomalies.map((a, i) => (
              <div key={`${a.date}-${a.type}`} className={`anom-row ${a.type}`} data-testid={`anom-${i}`}>
                <div className="anom-arrow">{a.type === "spike" ? "▲" : "▼"}</div>
                <div className="anom-date">{a.date}</div>
                <div className="anom-detail">
                  {a.type === "spike" ? "ارتفاع" : "انخفاض"} ملحوظ — الإيراد {fm(a.value)} ر · المتوسط {fm(a.average)} ر
                </div>
                <div className="anom-pct">{a.deviation_pct > 0 ? "+" : ""}{a.deviation_pct}%</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FORECAST */}
      {forecast.forecast?.length > 0 && (
        <>
          <div className="st">🔮 توقع الإيراد للأيام القادمة</div>
          <div className="card">
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
              مبني على متوسط أيام الأسبوع المماثلة آخر 28 يوم.
            </div>
            <div className="g3">
              {forecast.forecast.slice(0, 7).map((f) => (
                <div key={f.date} className="kc-pro brown" style={{ textAlign: "center", padding: "14px 12px" }}>
                  <div className="kpi-lbl">{formatKSADate(new Date(f.date), { weekday: "short", day: "numeric", month: "short" })}</div>
                  <div className="kpi-val" style={{ fontSize: 22 }}>{fm(f.forecast)}</div>
                  <div className="kpi-sub">ريال متوقع</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* CLV */}
      {clv?.top?.length > 0 && (
        <>
          <div className="st">💰 قيمة العميل مدى الحياة (CLV)</div>
          <div className="g2">
            <div className="kc-pro gold" style={{ textAlign: "center" }}>
              <div className="kpi-icn">💎</div>
              <div className="kpi-lbl">متوسط CLV</div>
              <div className="kpi-val">{fm(clv.avg_clv)}</div>
              <div className="kpi-sub">ريال للعميل · {fm(clv.total_customers)} عميل</div>
            </div>
            <div className="kc-pro green" style={{ textAlign: "center" }}>
              <div className="kpi-icn">📊</div>
              <div className="kpi-lbl">الوسيط (Median)</div>
              <div className="kpi-val">{fm(clv.median_clv)}</div>
              <div className="kpi-sub">ريال للعميل</div>
            </div>
          </div>
          <div className="card">
            <h4>🏆 أعلى 25 عميل من حيث الإنفاق الكلي (مفتاح الجوال)</h4>
            <div className="tw" style={{ marginTop: 8 }}>
              <table>
                <thead><tr><th>#</th><th>العميل</th><th>الجوال</th><th>الإنفاق الكلي</th><th>الزيارات</th><th>متوسط الفاتورة</th></tr></thead>
                <tbody>
                  {clv.top.slice(0, 15).map((c) => (
                    <tr key={c.phone || c.name}>
                      <td>{clv.top.indexOf(c) + 1}</td>
                      <td style={{ fontWeight: 600, textAlign: "right" }}>{c.name}</td>
                      <td className="num" style={{ direction: "ltr", textAlign: "right" }}>{c.phone || "—"}</td>
                      <td className="num">{fm(c.total)} ر</td>
                      <td>{fm(c.visits)}</td>
                      <td className="num">{fm(c.avg)} ر</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* BEST HOURS */}
      {best_hours?.length > 0 && (
        <>
          <div className="st">⏰ أكثر الساعات ربحاً</div>
          <div className="g3">
            {best_hours.map((h, i) => (
              <div key={`hour-${h.hour}-${i}`} className="kc-pro gold" style={{ textAlign: "center" }}>
                <div className="kpi-lbl">المركز #{i + 1}</div>
                <div className="kpi-val">{h.hour}:00</div>
                <div className="kpi-sub">{fm(h.rev)} ر · {fm(h.count)} فاتورة · متوسط {fm(h.avg_ticket)} ر</div>
              </div>
            ))}
          </div>
          <div className="card" style={{ marginTop: 14 }} data-testid="ai-hour-insights">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <h4 style={{ margin: 0 }}>🤖 تفسير ذكي لأنماط الساعات</h4>
              <button
                type="button"
                data-testid="ai-hour-insights-btn"
                className="btn btn-success"
                onClick={fetchHourAi}
                disabled={hourLoading}
                style={{ fontSize: 12.5 }}
              >
                {hourLoading ? "⏳ جارٍ التحليل..." : "✨ اطلب تحليل Claude"}
              </button>
            </div>
            {hourAi && (
              <div className="ai-md" style={{ marginTop: 14, fontSize: 13, lineHeight: 1.85, whiteSpace: "pre-wrap", color: "var(--brown-deep)" }}>
                {hourAi}
              </div>
            )}
            {!hourAi && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--muted)" }}>
                اضغط الزر للحصول على تفسير لساعات الذروة وأضعف الساعات + 3 توصيات عملية.
              </div>
            )}
          </div>
        </>
      )}

      {/* PER-SHIFT FORECAST */}
      {forecast_shift?.forecast?.length > 0 && (
        <>
          <div className="st">🔮 توقع الإيراد بالشِفت — الأسبوع القادم</div>
          <div className="card" data-testid="forecast-shift-card">
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
              متوسط آخر {forecast_shift.weeks_used} أسابيع لكل (شِفت × يوم أسبوع). الأساس: {forecast_shift.based_on_through}
            </div>
            <div className="tw">
              <table>
                <thead>
                  <tr>
                    <th>اليوم</th>
                    <th>التاريخ</th>
                    <th style={{ color: "rgba(30,99,144,.85)" }}>الصباحي 7ص-3م</th>
                    <th style={{ color: "rgba(194,169,127,.95)" }}>المسائي 3م-11م</th>
                    <th style={{ color: "rgba(107,78,61,.85)" }}>الليلي 11م-7ص</th>
                    <th>إجمالي اليوم</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast_shift.forecast.map((row) => (
                    <tr key={row.date}>
                      <td><b>{row.day_name}</b></td>
                      <td className="num" style={{ direction: "ltr" }}>{row.date}</td>
                      <td className="num">{fm(row.shifts.S1.forecast)} ر</td>
                      <td className="num">{fm(row.shifts.S2.forecast)} ر</td>
                      <td className="num">{fm(row.shifts.S3.forecast)} ر</td>
                      <td className="num"><b style={{ color: "var(--gold-deep)" }}>{fm(row.day_total)} ر</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* CHURN — Loyal customers we're losing */}
      {churn?.length > 0 && (
        <>
          <div className="st">📉 العملاء المنسحبون — أوفياء توقّفوا عن الزيارة</div>
          <div className="card" data-testid="churn-card">
            <div style={{ fontSize: 12.5, color: "var(--brown)", marginBottom: 10, lineHeight: 1.7 }}>
              <b>{fm(churn.length)}</b> عميل عنده <b>8+</b> زيارات سابقة لكنه ما زار خلال آخر 30 يوماً.
              هؤلاء أعلى أولوية لإعادة جذبهم بعرض WhatsApp استرجاع.
            </div>
            <div className="tw">
              <table>
                <thead><tr><th>#</th><th>الاسم</th><th>الجوال</th><th>الزيارات السابقة</th><th>إجمالي الإنفاق</th><th>آخر زيارة</th><th>منذ</th></tr></thead>
                <tbody>
                  {churn.slice(0, 20).map((c, i) => (
                    <tr key={(c.phone || c.name) + i}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 600, textAlign: "right" }}>{c.name}{c.is59 && <span style={{ fontSize: 10, color: "var(--gold)", marginRight: 4 }}>(59)</span>}</td>
                      <td className="num" style={{ direction: "ltr", textAlign: "right" }}>{c.phone || "—"}</td>
                      <td><b>{fm(c.visits)}</b></td>
                      <td className="num">{fm(c.total_spent)} ر</td>
                      <td className="num" style={{ direction: "ltr" }}>{c.last_visit || "—"}</td>
                      <td><span className={`b ${c.days_since && c.days_since < 60 ? "bo" : "br_"}`}>{c.days_since ? c.days_since + " يوم" : "—"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {churn.length > 20 && (
              <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 8 }}>
                + {fm(churn.length - 20)} عميل إضافي. صدّر القائمة الكاملة من تبويب العملاء.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
